import binIcon from '@src/main/assets/icons/bin-red.svg';
import editIcon from '@src/main/assets/icons/edit-red.svg';
import { AppStatusIncident } from '@src/main/modules/app_status/domain/appStatusIncident.model';
import { AppStatusFilterMeta } from '@src/main/modules/app_status/ui/filters/mapAppStatusFiltersToCriteria';
import { useAppStatusOptions } from '@src/main/modules/app_status/ui/hooks/useAppStatusOptions';
import { statusClassNames } from '@src/main/modules/app_status/ui/utils/appStatusIncidents.utils';
import { DateRangeFilter } from '@src/main/modules/messages/utils/templates';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import { Button } from 'primereact/button';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { DataTable, DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AppStatusErrorDescriptionBody from './AppStatusErrorDescriptionBody';
import styles from './AppStatusIncidents.module.scss';
import AppStatusPossibleSolutionBody from './AppStatusPossibleSolutionBody';

type AppStatusIncidentsTableProps = {
  canEdit: boolean;
  filters: AppStatusFilterMeta;
  first: number;
  incidents: AppStatusIncident[];
  isLoading: boolean;
  onDelete: (incident: AppStatusIncident) => void;
  onEdit: (incident: AppStatusIncident) => void;
  onFilter: (filters: AppStatusFilterMeta) => void;
  onPage: (event: DataTablePageEvent) => void;
  onSort: (event: DataTableSortEvent) => void;
  pageSize: number;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  total: number;
};

const AppStatusIncidentsTable: FC<AppStatusIncidentsTableProps> = ({
  canEdit,
  filters,
  first,
  incidents,
  isLoading,
  onDelete,
  onEdit,
  onFilter,
  onPage,
  onSort,
  pageSize,
  sortField,
  sortOrder,
  total,
}) => {
  const { t } = useTranslation();
  const { publicOptions, severityOptions, statusOptions } = useAppStatusOptions();

  const dropdownFilter = useCallback(
    (options: ColumnFilterElementTemplateOptions, values: { label: string; value: unknown }[]) => (
      <Dropdown
        value={options.value}
        options={values}
        onChange={(event) => options.filterApplyCallback(event.value)}
        showClear
      />
    ),
    [],
  );

  const dateFilter = useCallback(
    (options: ColumnFilterElementTemplateOptions) => (
      <DateRangeFilter
        value={options.value as (Date | null)[] | null}
        onChange={(value) => options.filterApplyCallback(value)}
        t={t}
      />
    ),
    [t],
  );

  const severityFilter = useCallback(
    (options: ColumnFilterElementTemplateOptions) => dropdownFilter(options, severityOptions),
    [dropdownFilter, severityOptions],
  );

  const statusFilter = useCallback(
    (options: ColumnFilterElementTemplateOptions) => dropdownFilter(options, statusOptions),
    [dropdownFilter, statusOptions],
  );

  const publicFilter = useCallback(
    (options: ColumnFilterElementTemplateOptions) => dropdownFilter(options, publicOptions),
    [dropdownFilter, publicOptions],
  );

  const severityBody = useCallback(
    (incident: AppStatusIncident) => (
      <span className={`${styles.tag} ${styles[`severity-${incident.severity}`]}`}>
        {t(`appStatus.severity.${incident.severity}`)}
      </span>
    ),
    [t],
  );

  const statusBody = useCallback(
    (incident: AppStatusIncident) => (
      <span className={`${styles.tag} ${styles[statusClassNames[incident.status]]}`}>
        {t(`appStatus.status.${incident.status}`)}
      </span>
    ),
    [t],
  );

  const publicBody = useCallback(
    (incident: AppStatusIncident) => (incident.public ? t('common.yes') : t('common.no')),
    [t],
  );

  const visibleAppBody = useCallback(
    (incident: AppStatusIncident) => (incident.visibleApp ? t('common.yes') : t('common.no')),
    [t],
  );

  const actions = useCallback(
    (incident: AppStatusIncident) => (
      <div className="flex gap-2">
        <Button
          icon={<img src={editIcon} alt={t('common.edit')} width="15" />}
          className="p-button-text"
          tooltip={t('common.edit')}
          onClick={() => onEdit(incident)}
        />
        <Button
          icon={<img src={binIcon} alt={t('common.delete')} width="15" />}
          className="p-button-text"
          tooltip={t('common.delete')}
          onClick={() => onDelete(incident)}
        />
      </div>
    ),
    [onDelete, onEdit, t],
  );

  return (
    <AppLoading isLoading={isLoading}>
      <Tooltip
        target={`.${styles['truncated-text']}`}
        className={styles['text-tooltip']}
        position="top"
        showDelay={300}
        hideDelay={100}
        autoHide={false}
      />
      <DataTable
        value={incidents}
        dataKey="id"
        lazy
        paginator
        loading={isLoading}
        filters={filters}
        onFilter={(event) => onFilter(event.filters as AppStatusFilterMeta)}
        first={first}
        rows={pageSize}
        rowsPerPageOptions={[10, 20, 50]}
        totalRecords={total}
        onPage={onPage}
        sortField={sortField}
        sortOrder={sortOrder === 'asc' ? 1 : -1}
        onSort={onSort}
        emptyMessage={t('common.emptyMessage')}
      >
        <Column field="code" header={t('appStatus.columns.code')} sortable filter />
        <Column field="version" header={t('appStatus.columns.version')} sortable filter />
        <Column
          field="date"
          header={t('appStatus.columns.date')}
          sortable
          filter
          filterElement={dateFilter}
          showFilterMatchModes={false}
        />
        <Column
          field="severity"
          header={t('appStatus.columns.severity')}
          sortable
          filter
          filterElement={severityFilter}
          showFilterMatchModes={false}
          body={severityBody}
        />
        <Column
          field="status"
          header={t('appStatus.columns.status')}
          sortable
          filter
          filterElement={statusFilter}
          showFilterMatchModes={false}
          body={statusBody}
        />
        <Column field="title" header={t('appStatus.columns.title')} sortable filter />
        <Column field="summary" header={t('appStatus.columns.summary')} sortable filter />
        <Column
          field="errorDescription"
          header={t('appStatus.columns.errorDescription')}
          sortable
          filter
          body={AppStatusErrorDescriptionBody}
        />
        <Column
          field="possibleSolution"
          header={t('appStatus.columns.possibleSolution')}
          sortable
          filter
          body={AppStatusPossibleSolutionBody}
        />
        <Column
          field="public"
          header={t('appStatus.columns.public')}
          sortable
          filter
          filterElement={publicFilter}
          showFilterMatchModes={false}
          body={publicBody}
        />
        <Column
          field="visibleApp"
          header={t('appStatus.columns.visibleApp')}
          sortable
          filter
          filterElement={publicFilter}
          showFilterMatchModes={false}
          body={visibleAppBody}
        />
        {canEdit && <Column body={actions} header={t('common.actions')} />}
      </DataTable>
    </AppLoading>
  );
};

export default AppStatusIncidentsTable;
